// src/components/SigningConfigPanel.jsx
import { useState, useEffect } from "react";
import Button from "./Button";
import { Input } from "./Input";
import { getSigningConfig, saveSigningConfig, deleteSigningConfig } from "../services/api";
import { useTranslation } from "react-i18next";

const EMAIL_MODES = [
  "employee",
  "employee_manager",
  "employee_manager_manager",
  "org_admin",
  "static",
];
const SIGNATURE_TYPES = ["advanced", "timestamp"];

function emptyParticipant(order) {
  return { order, signatureType: "advanced", emailMode: "employee", staticEmail: "", staticName: "" };
}
function emptyField(sortIndex) {
  return { sortIndex, participantOrder: 1, pageNumber: 1, xPosition: 0, yPosition: 0, width: 10, height: 5 };
}

function fromServerConfig(cfg) {
  return {
    flowType: cfg.flowType || "sequential",
    reviewType: cfg.reviewType || "single_reject",
    participants: (cfg.participants || []).map((p) => ({
      order: p.order,
      signatureType: p.signatureType || "advanced",
      emailMode: p.emailMode || "employee",
      staticEmail: p.staticEmail || "",
      staticName: p.staticName || "",
    })),
    fields: (cfg.fields || []).map((f) => ({
      sortIndex: f.sortIndex,
      participantOrder: f.participantOrder,
      pageNumber: f.pageNumber,
      xPosition: f.xPosition,
      yPosition: f.yPosition,
      width: f.width,
      height: f.height,
    })),
  };
}

const DEFAULT_FORM = {
  flowType: "sequential",
  reviewType: "single_reject",
  participants: [emptyParticipant(1)],
  fields: [emptyField(1)],
};

export default function SigningConfigPanel({ trainingSlug }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState("SSM");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [flowType, setFlowType] = useState(DEFAULT_FORM.flowType);
  const [reviewType, setReviewType] = useState(DEFAULT_FORM.reviewType);
  const [participants, setParticipants] = useState(DEFAULT_FORM.participants);
  const [fields, setFields] = useState(DEFAULT_FORM.fields);

  useEffect(() => {
    loadConfig(kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, trainingSlug]);

  async function loadConfig(k) {
    setLoading(true);
    setErr("");
    setSuccessMsg("");
    try {
      const cfg = await getSigningConfig(trainingSlug, k);
      if (cfg) {
        const parsed = fromServerConfig(cfg);
        setFlowType(parsed.flowType);
        setReviewType(parsed.reviewType);
        setParticipants(parsed.participants.length ? parsed.participants : [emptyParticipant(1)]);
        setFields(parsed.fields.length ? parsed.fields : [emptyField(1)]);
      } else {
        setFlowType(DEFAULT_FORM.flowType);
        setReviewType(DEFAULT_FORM.reviewType);
        setParticipants([emptyParticipant(1)]);
        setFields([emptyField(1)]);
      }
    } catch (e) {
      setErr(e?.message || t("signing_config.err_load", "Failed to load config"));
    } finally {
      setLoading(false);
    }
  }

  /* --- Participants helpers --- */
  function updateParticipant(i, patch) {
    setParticipants((arr) => {
      const copy = [...arr];
      copy[i] = { ...copy[i], ...patch };
      return copy;
    });
  }
  function addParticipant() {
    setParticipants((arr) => [...arr, emptyParticipant(arr.length + 1)]);
  }
  function removeParticipant(i) {
    setParticipants((arr) => arr.filter((_, idx) => idx !== i));
  }

  /* --- Fields helpers --- */
  function updateField(i, patch) {
    setFields((arr) => {
      const copy = [...arr];
      copy[i] = { ...copy[i], ...patch };
      return copy;
    });
  }
  function addField() {
    setFields((arr) => [...arr, emptyField(arr.length + 1)]);
  }
  function removeField(i) {
    setFields((arr) => arr.filter((_, idx) => idx !== i));
  }

  async function handleSave() {
    setSaving(true);
    setErr("");
    setSuccessMsg("");
    try {
      await saveSigningConfig(trainingSlug, {
        certificateKind: kind,
        flowType,
        reviewType,
        participants: participants.map((p) => ({
          order: Number(p.order),
          signatureType: p.signatureType,
          emailMode: p.emailMode,
          staticEmail: p.emailMode === "static" ? p.staticEmail || null : null,
          staticName: p.emailMode === "static" ? (p.staticName || null) : null,
        })),
        fields: fields.map((f) => ({
          sortIndex: Number(f.sortIndex),
          participantOrder: Number(f.participantOrder),
          pageNumber: Number(f.pageNumber),
          xPosition: Number(f.xPosition),
          yPosition: Number(f.yPosition),
          width: Number(f.width),
          height: Number(f.height),
        })),
      });
      setSuccessMsg(t("signing_config.saved", "Config saved"));
      await loadConfig(kind);
    } catch (e) {
      setErr(e?.message || t("signing_config.err_save", "Failed to save config"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t("signing_config.confirm_delete", "Delete signing config for {{kind}}?", { kind }))) return;
    setDeleting(true);
    setErr("");
    setSuccessMsg("");
    try {
      await deleteSigningConfig(trainingSlug, kind);
      setFlowType(DEFAULT_FORM.flowType);
      setReviewType(DEFAULT_FORM.reviewType);
      setParticipants([emptyParticipant(1)]);
      setFields([emptyField(1)]);
      setSuccessMsg(t("signing_config.deleted", "Config deleted"));
    } catch (e) {
      setErr(e?.message || t("signing_config.err_delete", "Failed to delete config"));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Kind tabs */}
      <div className="flex gap-2">
        {["SSM", "SU"].map((k) => (
          <button
            key={k}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              kind === k
                ? "bg-black text-white border-black"
                : "border-black/10 text-black/70 hover:bg-black/5"
            }`}
            onClick={() => setKind(k)}
          >
            {k}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-black/60">{t("signing_config.loading", "Loading…")}</div>
      ) : (
        <>
          {err && <div className="text-sm text-red-600">{err}</div>}
          {successMsg && <div className="text-sm text-green-700">{successMsg}</div>}

          {/* Flow / Review type */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-black/60 mb-1">{t("signing_config.flow_type", "Flow type")}</label>
              <select
                className="px-3 py-2 rounded-xl border border-black/10 text-sm w-full"
                value={flowType}
                onChange={(e) => setFlowType(e.target.value)}
              >
                <option value="sequential">sequential</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-black/60 mb-1">{t("signing_config.review_type", "Review type")}</label>
              <select
                className="px-3 py-2 rounded-xl border border-black/10 text-sm w-full"
                value={reviewType}
                onChange={(e) => setReviewType(e.target.value)}
              >
                <option value="single_reject">single_reject</option>
              </select>
            </div>
          </div>

          {/* Participants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">{t("signing_config.participants", "Participants")}</div>
              <Button size="sm" variant="ghost" onClick={addParticipant}>
                {t("signing_config.add_participant", "+ Add participant")}
              </Button>
            </div>
            <div className="space-y-2">
              {participants.map((p, i) => (
                <div key={i} className="p-3 rounded-xl border border-black/10 grid sm:grid-cols-4 gap-2 items-end">
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.order", "Order")}</label>
                    <Input
                      type="number"
                      min={1}
                      value={p.order}
                      onChange={(e) => updateParticipant(i, { order: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.signature_type", "Signature type")}</label>
                    <select
                      className="px-3 py-2 rounded-xl border border-black/10 text-sm w-full"
                      value={p.signatureType}
                      onChange={(e) => updateParticipant(i, { signatureType: e.target.value })}
                    >
                      {SIGNATURE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.email_mode", "Email mode")}</label>
                    <select
                      className="px-3 py-2 rounded-xl border border-black/10 text-sm w-full"
                      value={p.emailMode}
                      onChange={(e) => updateParticipant(i, { emailMode: e.target.value })}
                    >
                      {EMAIL_MODES.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeParticipant(i)}
                      disabled={participants.length <= 1}
                    >
                      {t("signing_config.remove", "Remove")}
                    </Button>
                  </div>
                  {p.emailMode === "static" && (
                    <>
                      <div className="sm:col-span-2">
                        <label className="block text-xs text-black/60 mb-1">
                          {t("signing_config.static_email", "Static email")} *
                        </label>
                        <Input
                          value={p.staticEmail}
                          onChange={(e) => updateParticipant(i, { staticEmail: e.target.value })}
                          placeholder="admin@company.com"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs text-black/60 mb-1">
                          {t("signing_config.static_name", "Name (optional)")}
                        </label>
                        <Input
                          value={p.staticName}
                          onChange={(e) => updateParticipant(i, { staticName: e.target.value })}
                          placeholder="Admin"
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Signature fields */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">{t("signing_config.fields", "Signature fields")}</div>
              <Button size="sm" variant="ghost" onClick={addField}>
                {t("signing_config.add_field", "+ Add field")}
              </Button>
            </div>
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={i} className="p-3 rounded-xl border border-black/10 grid sm:grid-cols-4 gap-2 items-end">
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.field_sort_index", "Sort index")}</label>
                    <Input
                      type="number"
                      min={1}
                      value={f.sortIndex}
                      onChange={(e) => updateField(i, { sortIndex: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.field_participant", "Participant order")}</label>
                    <select
                      className="px-3 py-2 rounded-xl border border-black/10 text-sm w-full"
                      value={f.participantOrder}
                      onChange={(e) => updateField(i, { participantOrder: e.target.value })}
                    >
                      {participants.map((p) => (
                        <option key={p.order} value={p.order}>{p.order}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.field_page", "Page")}</label>
                    <Input
                      type="number"
                      min={1}
                      value={f.pageNumber}
                      onChange={(e) => updateField(i, { pageNumber: e.target.value })}
                    />
                  </div>
                  <div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeField(i)}
                      disabled={fields.length <= 1}
                    >
                      {t("signing_config.remove", "Remove")}
                    </Button>
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.field_x", "X (%)")}</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={f.xPosition}
                      onChange={(e) => updateField(i, { xPosition: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.field_y", "Y (%)")}</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={f.yPosition}
                      onChange={(e) => updateField(i, { yPosition: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.field_width", "Width (%)")}</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={f.width}
                      onChange={(e) => updateField(i, { width: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">{t("signing_config.field_height", "Height (%)")}</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={f.height}
                      onChange={(e) => updateField(i, { height: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving || deleting}>
              {saving ? t("signing_config.saving", "Saving…") : t("signing_config.save", "Save config")}
            </Button>
            <Button variant="ghost" onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? t("signing_config.deleting", "Deleting…") : t("signing_config.delete", "Delete config")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
